import React, { useMemo, useRef } from 'react';
import { PropTypes } from 'prop-types';
import noop from 'lodash/noop';
import iconix from '@decisiv/iconix/lib/components';
import { t, Trans } from '@lingui/macro';
import {
  Checkbox,
  Tag,
  P,
  Flex,
  Popover,
  Button,
  Tooltip,
} from '@decisiv/ui-components';

import { IntegrationContainerWrapper } from '../../pages/IntegrationsPage/style';
import { ArchiveCardFooter } from './ArchiveCardFooter.styled';
import { CardTitle } from './CardTitle.styled';
import { CardLine } from './CardLine.styled';
import { SeparatorX } from './style';
import { formatTimestamp } from '../../utils/dates';
import usePopoverToggle from '../../hooks/usePopoverToggle';
import linkedIntegrations from '../../utils/linkedIntegrations';

export default function IntegrationContainer({
  integration,
  integrationId = '',
  locationUrl,
  archiveMode = false,
  allSharedSettings = [],
  onRestore = noop,
  selectable = false,
  selected = false,
  selectionLimitReached = false,
  onToggleSelect = noop,
}) {
  // These were `useCallback(setX, [x])` — keyed on the very state they set, so
  // every popover update gave the ref a new identity and React detached and
  // re-attached it, burning a render cycle each time.
  const triggerHttpPopover = usePopoverToggle();
  const actionRefTriggerHttpRef = useRef();

  const serviceNetworkPopover = usePopoverToggle();
  const actionRefServiceNetworkRef = useRef();

  const applicationEventPopover = usePopoverToggle();
  const actionRefApplicationEventRef = useRef();

  const linkedPopover = usePopoverToggle();
  const actionRefLinkedRef = useRef();

  const MAX_DESCRIPTION_SIZE = 85;

  const linked = useMemo(
    () => linkedIntegrations(integrationId, allSharedSettings),
    [integrationId, allSharedSettings],
  );

  return (
    <IntegrationContainerWrapper
      flexDirection="column"
      size="MD"
      name="integration-container"
      selected={selected}
    >
      <Flex padding={2} flexDirection="column" size="MD">
        {selectable && (
          <Flex name="selectIntegration" justifyContent="flex-end">
            <Checkbox
              name={`select-integration-${integrationId || integration.name}`}
              aria-label={t`Select ${integration.name}`}
              value="selected"
              checked={selected}
              disabled={selectionLimitReached && !selected}
              onChange={() => onToggleSelect()}
            />
          </Flex>
        )}
        <Flex
          name="titleAndDesc"
          flexDirection="column"
          style={{ height: '5.625rem' }}
        >
          <CardTitle to={locationUrl} text={integration.name} />

          <Flex flexDirection="row">
            <P color="alaskanHusky" weight="regular">
              {integration.description.substring(0, MAX_DESCRIPTION_SIZE)}
              {integration.description.length >= MAX_DESCRIPTION_SIZE
                ? '...'
                : ''}
            </P>
          </Flex>
        </Flex>

        {!archiveMode && (
          <Flex name="infoTags" marginTop={1} marginLeft={-1.5}>
            <Flex alignItems="flex-start">
              <Tag
                text={integration.enabled ? 'Active' : 'Inactive'}
                icon={iconix.CircleF}
                color={integration.enabled ? 'success' : 'licoriceMousse'}
                variant="outline"
              />
            </Flex>

            {integration.cache_enabled && (
              <Flex alignItems="flex-start">
                <Tag
                  style={{
                    marginLeft: '0.2rem',
                  }}
                  text="Cache"
                  palette="pastel"
                  icon={iconix.CheckCircleF}
                  color="success"
                />
              </Flex>
            )}

            {integration.automatic_retry_enabled && (
              <Flex alignItems="flex-start">
                <Tag
                  style={{
                    marginLeft: '0.2rem',
                  }}
                  text="Auto Retry"
                  palette="pastel"
                  icon={iconix.CheckCircleF}
                  color="success"
                />
              </Flex>
            )}

            {integration.circuit_breaker_enabled &&
              integration.circuit_breaker_current_enabled && (
                <Tooltip
                  target={
                    <Flex alignItems="flex-start">
                      <Tag
                        style={{
                          marginLeft: '0.2rem',
                        }}
                        text="Circuit Breaker"
                        color="danger"
                        Tooltip
                      />
                    </Flex>
                  }
                >
                  {t` ${formatTimestamp(
                    integration.circuit_breaker_opened_at,
                  )}`}
                </Tooltip>
              )}

            {integration.circuit_breaker_enabled &&
              !integration.circuit_breaker_current_enabled && (
                <Flex alignItems="flex-start">
                  <Tag
                    style={{
                      marginLeft: '0.2rem',
                    }}
                    text="Circuit Breaker"
                    color="success"
                    palette="pastel"
                    icon={iconix.CheckCircleF}
                  />
                </Flex>
              )}
          </Flex>
        )}
      </Flex>
      <>
        <CardLine>
          <Flex flexDirection="row" style={{ width: '110px' }}>
            <P color="alaskanHusky" weight="medium" size="small">
              <Trans>Category</Trans>
            </P>
          </Flex>

          <Flex flexDirection="row">
            <P color="charcoal" weight="medium" size="small">
              {integration.category}
            </P>
          </Flex>
        </CardLine>

        <SeparatorX />
        <CardLine>
          <Flex flexDirection="row" style={{ width: '110px' }}>
            <P color="alaskanHusky" weight="medium" size="small">
              <Trans>Audience</Trans>
            </P>
          </Flex>

          <Flex flexDirection="row">
            <P color="charcoal" weight="medium" size="small">
              {integration.audience.length === 1
                ? integration.audience.join(';')
                : t`Asset and Service Management`}
            </P>
          </Flex>
        </CardLine>

        <SeparatorX />
        <CardLine>
          <Flex flexDirection="row" style={{ width: '110px' }}>
            <P color="alaskanHusky" weight="medium" size="small">
              <Trans>Service Network</Trans>
            </P>
          </Flex>

          <Flex flexDirection="row">
            <Flex>
              <P color="charcoal" weight="medium" size="small">
                {integration.service_network.length === 0
                  ? t`None`
                  : integration.service_network.sort().slice(0, 2).join('; ')}
              </P>
            </Flex>

            {integration.service_network.length > 2 && (
              <Flex marginLeft={1} marginTop={-0.5}>
                <Tag
                  action={serviceNetworkPopover.toggle}
                  actionAttributes={{ 'aria-label': 'Expand' }}
                  actionRef={actionRefServiceNetworkRef}
                  actionIcon={iconix.ChevronDown}
                  color="information"
                  text={`+${integration.service_network
                    ?.slice(2)
                    .length.toString()}`}
                  variant="outline"
                  style={{ float: 'right' }}
                />
                <Popover
                  ref={serviceNetworkPopover.popoverRef}
                  target={actionRefServiceNetworkRef}
                  manageEvents="click"
                >
                  <Flex padding={1} style={{ width: '18.75rem' }}>
                    <P color="alaskanHusky">
                      {integration.service_network?.sort().slice(2).join('; ')}
                    </P>
                  </Flex>
                </Popover>
              </Flex>
            )}
          </Flex>
        </CardLine>

        <SeparatorX />
        <CardLine>
          <Flex flexDirection="row" style={{ width: '110px' }}>
            <P color="alaskanHusky" weight="medium" size="small">
              <Trans>Trigger</Trans>
            </P>
          </Flex>

          <Flex flexDirection="row">
            {integration.trigger_type.includes('http_api_request') && (
              <Flex marginTop={-0.5}>
                <Tag
                  action={triggerHttpPopover.toggle}
                  actionAttributes={{ 'aria-label': 'Expand' }}
                  actionRef={actionRefTriggerHttpRef}
                  actionIcon={iconix.ChevronDown}
                  color="information"
                  text="http"
                  variant="outline"
                  style={{ float: 'right' }}
                />
                <Popover
                  ref={triggerHttpPopover.popoverRef}
                  target={actionRefTriggerHttpRef}
                  manageEvents="click"
                >
                  <Flex padding={1}>
                    <P color="alaskanHusky">{integration.action}</P>
                  </Flex>
                </Popover>
              </Flex>
            )}

            {integration.events.length > 0 && (
              <Flex marginTop={-0.5} marginLeft={1}>
                <Tag
                  action={applicationEventPopover.toggle}
                  actionAttributes={{ 'aria-label': 'Expand' }}
                  actionRef={actionRefApplicationEventRef}
                  actionIcon={iconix.ChevronDown}
                  color="information"
                  text="applitcation_event"
                  variant="outline"
                  style={{ float: 'right' }}
                />
                <Popover
                  ref={applicationEventPopover.popoverRef}
                  target={actionRefApplicationEventRef}
                  manageEvents="click"
                >
                  <Flex padding={1}>
                    <P color="alaskanHusky">
                      {integration.events?.sort().map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </P>
                  </Flex>
                </Popover>
              </Flex>
            )}
          </Flex>
        </CardLine>
        <SeparatorX />
        <CardLine>
          <Flex flexDirection="row" style={{ width: '110px' }}>
            <P color="alaskanHusky" weight="medium" size="small">
              <Trans>Linked</Trans>
            </P>
          </Flex>
          <Flex flexDirection="row">
            {linked.length === 0 ? (
              <P color="charcoal" weight="medium" size="small">
                <Trans>None</Trans>
              </P>
            ) : (
              <Flex marginTop={-0.5}>
                <Tag
                  action={linkedPopover.toggle}
                  actionAttributes={{
                    'aria-label': t`Show linked integrations`,
                  }}
                  actionRef={actionRefLinkedRef}
                  actionIcon={iconix.ChevronDown}
                  icon={iconix.Link}
                  color="information"
                  text={`${linked.length}`}
                  variant="outline"
                />
                <Popover
                  ref={linkedPopover.popoverRef}
                  target={actionRefLinkedRef}
                  manageEvents="click"
                >
                  <Flex padding={1} flexDirection="column">
                    {linked.map((integ) => (
                      <P key={integ.uuid} color="alaskanHusky">
                        {integ.name}
                      </P>
                    ))}
                  </Flex>
                </Popover>
              </Flex>
            )}
          </Flex>
        </CardLine>

        {archiveMode && (
          <>
            <SeparatorX />
            <ArchiveCardFooter>
              <Flex flexDirection="row">
                <Flex flexDirection="column" justifyContent="center">
                  <Flex
                    flexDirection="column"
                    paddingX={2}
                    paddingY={1}
                    flexShrink={1}
                  >
                    <P color="alaskanHusky" weight="medium">
                      <Trans>Archived by {integration.discarded_by}</Trans>
                    </P>
                    <P color="alaskanHusky" weight="regular">
                      {integration.discarded_at}
                    </P>
                  </Flex>
                </Flex>
              </Flex>
              <Flex marginRight={1} paddingY={0.5}>
                <Button
                  type="button"
                  id={`restore-${integrationId}`}
                  text="restore"
                  kind="primary"
                  variant="ghost"
                  onClick={onRestore}
                />
              </Flex>
            </ArchiveCardFooter>
          </>
        )}
      </>
    </IntegrationContainerWrapper>
  );
}

IntegrationContainer.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  integrationId: PropTypes.string,
  locationUrl: PropTypes.string.isRequired,
  archiveMode: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  allSharedSettings: PropTypes.array,
  onRestore: PropTypes.func,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  selectionLimitReached: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};
